import ConductionModal from "@/components/modals/ConductionModal";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Classroom, Role, User } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import {
    Badge,
    Button,
    Flex,
    Group,
    Pagination,
    ScrollArea,
    Skeleton,
    Table,
    Text,
    Tooltip,
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconEdit } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import StudentPasswordUpdateForm from "./StudentPasswordUpdateForm";

interface ClassroomUsersBrowserProps {
    classroom: Classroom;
    defaultRoleFilter?: Role;
    onClose: () => void;
}

const ClassroomUsersBrowser: React.FC<ClassroomUsersBrowserProps> = ({ classroom, defaultRoleFilter, onClose }) => {
    const [users, setUsers] = useState<User[] | null>(null);
    const [desiredPage, setDesiredPage] = useState<number>(1);
    const [usersPerPage, setUsersPerPage] = useState<number>(6);
    const [loading, setLoading] = useState<boolean>(true);

    const [roleFilter, _] = useState<Role | null>(defaultRoleFilter ?? null);

    const auth = useContext(AuthContext);
    if (auth === undefined) {
        throw new Error("ProjectSelect must be a child of AuthContext.");
    }
    const token = auth.token;
    if (token === null) {
        return;
    }

    const handleTutorialCompletionOverride = async (user: User, completionStatus: boolean) => {
        setLoading(true);
        // Display a notification that deletion is in progress.
        const notificationId = Utils.displayNotification(
            "Updating tutorial completion status...",
            undefined,
            "blue",
            "loading"
        );

        try {
            // Call the API to delete the classroom.
            await ConductionAPI.setTutorialCompletionStatus(user.id, completionStatus, token);

            Utils.updateNotification(
                notificationId,
                "Tutorial completion status updated!",
                undefined,
                "green",
                <IconCheck size={16} />
            );
        } catch (error) {
            console.error("Error creating tokens", error);
            Utils.updateNotification(
                notificationId,
                "We are sorry. An unexpected error occurred when updating the tutorial completion status.",
                undefined,
                "red",
                <IconAlertCircle size={16} />,
                true,
                false
            );
        } finally {
            fetchClassroom();
        }
    }

    const height = 60; // view height (vh)
    const calculateUsersPerPage = useCallback(() => {
        const rowHeight = 45; // pixel estimation
        const containerHeight = window.innerHeight * (height / 100) - 100; // Use 60% of the viewport height minus some estimate for other elements
        setUsersPerPage(Math.floor(containerHeight / rowHeight));

        if (users && Math.floor(containerHeight / rowHeight) * (desiredPage - 1) + 1 > users.length) {
            setDesiredPage(1);
        }
    }, [desiredPage, window.innerHeight]);

    useEffect(() => {
        calculateUsersPerPage();
        window.addEventListener("resize", calculateUsersPerPage);
        return () => window.removeEventListener("resize", calculateUsersPerPage);
    }, [calculateUsersPerPage]);

    const fetchClassroom = () => {
        const notificationId = Utils.displayNotification(
            "Loading Users",
            "Fetching user list, please wait...",
            "blue",
            "loading"
        );

        ConductionAPI.getClassroomUsers(classroom.id, token)
            .then((loadedUsersInfo: User[] | null) => {
                if (!loadedUsersInfo || loadedUsersInfo.length === 0) {
                    setUsers(null);
                    Utils.updateNotification(
                        notificationId,
                        "No Users Found",
                        "No users are currently available.",
                        "red",
                        <IconAlertCircle size={16} />,
                        true
                    );
                } else {
                    if (roleFilter != null) {
                        loadedUsersInfo = loadedUsersInfo.filter(user => user.roles.includes(roleFilter));
                    }
                    setUsers(
                        loadedUsersInfo
                            .sort((user1, user2) => user1.username.localeCompare(user2.username)) // Alphabetize by username
                            .sort((user1, user2) => (user1.roles.includes("Student") && !user2.roles.includes("Student") ? -1 : 1)) // Students first
                    );
                    Utils.updateNotification(
                        notificationId,
                        "Users Loaded",
                        "User list retrieved.",
                        "green",
                        <IconCheck size={16} />
                    );
                }
            })
            .catch(() => {
                Utils.updateNotification(
                    notificationId,
                    "Error Fetching Users",
                    "An error occurred while retrieving users.",
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            })
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        fetchClassroom();
    }, [classroom.id, token]);

    const paginatedUsers = useMemo(() => {
        const start = (desiredPage - 1) * usersPerPage;
        return users?.slice(start, start + usersPerPage) ?? [];
    }, [desiredPage, users, usersPerPage]);

    const [showStudentPasswordUpdateFormFor, setShowStudentPasswordUpdateFormFor] = useState<string | null>(null);
    const renderModals = () => (
        <>
            {showStudentPasswordUpdateFormFor && (
                <StudentPasswordUpdateForm
                    userId={showStudentPasswordUpdateFormFor}
                    onClose={() => setShowStudentPasswordUpdateFormFor(null)}
                />
            )}
        </>
    );

    return (
        <ConductionModal
            title={"Classroom Users"}
            size={"lg"}
            onClose={onClose}
        >
            {renderModals()}
            <Flex
                direction={"column"}
                align="center"
                justify="space-between"
                style={{
                    height: `${height}vh`
                }}
            >
                {loading ? (
                    <Skeleton height={"100%"} radius="md" />
                ) : users === null ? (
                    <Text>No users found.</Text>
                ) : (
                    <ScrollArea
                        style={{
                            width: "100%"
                        }}
                    >
                        <Table
                            striped
                            stickyHeader
                            highlightOnHover
                        >
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Username</Table.Th>
                                    <Table.Th>Role</Table.Th>
                                    <Table.Th>Completed Intro Tutorial?</Table.Th>
                                    <Table.Th>Actions</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {paginatedUsers.map((user) => {
                                    const student = user.roles.includes("Student");
                                    const classroomAdmin = user.roles.includes("ClassroomAdmin");
                                    const conductionAdmin = user.roles.includes("ConductionAdmin");
                                    return (
                                        <Table.Tr key={user.username}>
                                            <Table.Td>{user.username}</Table.Td>
                                            <Table.Td>
                                                {(student) && (
                                                    <Badge color="blue">
                                                        Student
                                                    </Badge>
                                                )}
                                                {(classroomAdmin) && (
                                                    <Badge color="yellow">
                                                        Instructor
                                                    </Badge>
                                                )}
                                                {(conductionAdmin) && (
                                                    <Badge color="darkGreen">
                                                        Conduction Admin
                                                    </Badge>
                                                )}
                                                {(!student && !classroomAdmin && !conductionAdmin) && (
                                                    <Badge color="red">
                                                        Other
                                                    </Badge>
                                                )}
                                            </Table.Td>
                                            <Table.Td>
                                                <Group>
                                                    <Badge color={user.completedBeginnerTutorial ? "green" : "red"}>
                                                        {user.completedBeginnerTutorial ? "Yes" : "No"}
                                                    </Badge>
                                                    <Tooltip
                                                        withArrow
                                                        label={user.completedBeginnerTutorial ? `Reset intro tutorial for ${user.username}` : `Mark the intro tutorial as completed for ${user.username}`}
                                                    >
                                                        <Button
                                                            size="xs"
                                                            onClick={() => handleTutorialCompletionOverride(user, !user.completedBeginnerTutorial)}
                                                        >
                                                            <IconEdit size={18} />
                                                        </Button>
                                                    </Tooltip>
                                                </Group>
                                            </Table.Td>
                                            <Table.Td>
                                                <Button
                                                    size="xs"
                                                    disabled={!student || classroomAdmin || conductionAdmin}
                                                    onClick={() => setShowStudentPasswordUpdateFormFor(user.id)}
                                                >
                                                    Set New Password
                                                </Button>
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                )}

                {users && users.length > usersPerPage && (
                    <Pagination
                        total={Math.ceil(users.length / usersPerPage)}
                        value={desiredPage}
                        onChange={setDesiredPage}
                    />
                )}
            </Flex>
        </ConductionModal>
    );
};

export default ClassroomUsersBrowser;
